// ContractPopup.js - Enhanced with inline signing for simple contracts, mandatory enforcement, preview feature, and iOS safe area support
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import {
  Assignment as ContractIcon,
  Description as DocumentIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Launch as LaunchIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Message as MessageIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PictureAsPdf as PdfIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import client from "../../../API";

const BLITZ_ROOT = process.env.REACT_APP_PUBLIC_ROOT || window.location.origin;
const buildPublicLink = (assignmentId) => `${BLITZ_ROOT}/contract/${assignmentId}`;

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    alert("Link copied!");
  } catch {
    alert("Failed to copy link");
  }
};

function normalizeContracts(contracts) {
  return contracts
    .map((item) => {
      if (item?.template) {
        return {
          assignmentId: item.id,
          signatureId: item.signature?.id || item.id,
          assignment: item,
          template: item.template || {},
          isSimple: item.is_simple,
        };
      }
      const assignment = item?.assignment || {};
      return {
        assignmentId: assignment.id || item.assignment_id,
        signatureId: item.signature?.id || item.id,
        assignment,
        template: assignment.template || {},
        isSimple: assignment.is_simple,
      };
    })
    .filter((c) => c.assignmentId);
}

export function ContractPopup({ 
  open, 
  contracts = [], 
  onClose, 
  hideReviewAll = false,
  onContractsUpdated = null,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [pending, setPending] = useState(() => normalizeContracts(contracts));
  const [selectedContracts, setSelectedContracts] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [processingContract, setProcessingContract] = useState(null);

  // Preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAssignment, setPreviewAssignment] = useState(null);
  const [previewSignature, setPreviewSignature] = useState(null);
  const [previewContractData, setPreviewContractData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewActionLoading, setPreviewActionLoading] = useState(false);

  useEffect(() => {
    setPending(normalizeContracts(contracts));
  }, [contracts]);

  if (!open || !pending.length) return null;

  const hasMultiple = pending.length > 1;
  const hasAnySimple = pending.some(c => c.isSimple);
  const hasMandatorySimple = pending.some(c => c.template?.is_mandatory && c.isSimple);

  const hasSelectedMandatory = Array.from(selectedContracts).some(id => {
    const c = pending.find(c => c.signatureId === id);
    return c?.template?.is_mandatory;
  });

  // Handler functions
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedContracts(new Set(pending.filter(c => c.isSimple).map(c => c.signatureId)));
    } else {
      setSelectedContracts(new Set());
    }
  };

  const handleSelectContract = (signatureId) => {
    const newSelected = new Set(selectedContracts);
    if (newSelected.has(signatureId)) {
      newSelected.delete(signatureId);
    } else {
      newSelected.add(signatureId);
    }
    setSelectedContracts(newSelected);
  };

  const agreeToContract = async (signatureId) => {
    try {
      await client.contracts.agreeToContract(signatureId);
      return { success: true, signatureId };
    } catch (error) {
      console.error(`Error agreeing to contract ${signatureId}:`, error);
      return { success: false, signatureId, error: error.message || 'Unknown error' };
    }
  };

  const declineContract = async (signatureId, reason = "Declined from popup") => {
    try {
      await client.contracts.declineContract(signatureId, reason);
      return { success: true, signatureId };
    } catch (error) {
      console.error(`Error declining contract ${signatureId}:`, error);
      return { success: false, signatureId, error: error.message || 'Unknown error' };
    }
  };

  const handleAcceptAll = async () => {
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    const contractsToAccept = hasMultiple 
      ? Array.from(selectedContracts)
      : pending.filter(c => c.isSimple).map(c => c.signatureId);

    if (contractsToAccept.length === 0) {
      setErrorMessage("Please select at least one contract to accept");
      setLoading(false);
      return;
    }

    const results = await Promise.all(
      contractsToAccept.map(signatureId => agreeToContract(signatureId))
    );

    const successful = results.filter(r => r.success).length;
    const failed = results.filter(r => !r.success);

    const successfulIds = results.filter(r => r.success).map(r => r.signatureId);

    setPending(prev => prev.filter(c => !successfulIds.includes(c.signatureId)));

    setLoading(false);

    if (failed.length === 0) {
      setSuccessMessage(`Successfully accepted ${successful} contract${successful > 1 ? 's' : ''}!`);
      setSelectedContracts(new Set());
      
      // Call update callback if provided
      if (onContractsUpdated) {
        setTimeout(() => {
          onContractsUpdated();
          onClose();
        }, 1500);
      } else {
        setTimeout(onClose, 2000);
      }
    } else {
      setErrorMessage(`Accepted ${successful} contract(s), but ${failed.length} failed`);
    }
  };

  const handleDeclineAll = async () => {
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    const contractsToDecline = hasMultiple 
      ? Array.from(selectedContracts)
      : pending.filter(c => c.isSimple).map(c => c.signatureId);

    if (contractsToDecline.length === 0) {
      setErrorMessage("Please select at least one contract to decline");
      setLoading(false);
      return;
    }

    const results = await Promise.all(
      contractsToDecline.map(signatureId => declineContract(signatureId))
    );

    const successful = results.filter(r => r.success).length;
    const failed = results.filter(r => !r.success);

    const successfulIds = results.filter(r => r.success).map(r => r.signatureId);

    setPending(prev => prev.filter(c => !successfulIds.includes(c.signatureId)));

    setLoading(false);

    if (failed.length === 0) {
      setSuccessMessage(`Successfully declined ${successful} contract${successful > 1 ? 's' : ''}!`);
      setSelectedContracts(new Set());
      
      if (onContractsUpdated) {
        setTimeout(() => {
          onContractsUpdated();
          onClose();
        }, 1500);
      } else {
        setTimeout(onClose, 2000);
      }
    } else {
      setErrorMessage(`Declined ${successful} contract(s), but ${failed.length} failed`);
    }
  };

  const handleAcceptSingle = async (signatureId) => {
    setProcessingContract(signatureId);
    setErrorMessage(null);
    
    const result = await agreeToContract(signatureId);
    
    setProcessingContract(null);
    
    if (result.success) {
      setSuccessMessage("Contract accepted successfully!");
      setPending(prev => prev.filter(c => c.signatureId !== signatureId));
      if (onContractsUpdated) {
        setTimeout(() => {
          onContractsUpdated();
        }, 1000);
      }
    } else {
      setErrorMessage(`Failed to accept contract: ${result.error}`);
    }
  };

  const handleViewAll = () => {
    navigate("/user?tab=contracts");
    onClose();
  };

  // Preview handlers
  const openPreview = async (item) => {
    let assignment;
    let signature = null;

    if (item?.template) {
      assignment = item;
    } else {
      assignment = item.assignment;
      signature = { id: item.signatureId }; // Minimal signature for pending
    }

    if (!assignment?.id) {
      alert('Invalid assignment ID - cannot load preview');
      return;
    }

    setPreviewAssignment(assignment);
    setPreviewSignature(signature);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewContractData(null);

    try {
      const fullContractData = await client.contracts.getContractFromAssignment(assignment.id);
      setPreviewContractData(fullContractData);
    } catch (error) {
      console.error('Failed to load contract data:', error);
      alert('Failed to load contract preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewAssignment(null);
    setPreviewSignature(null);
    setPreviewContractData(null);
    setPreviewLoading(false);
  };

  const handlePreviewAgree = async (sig, isSimple) => {
    const sigId = sig?.id || previewSignature?.id;
    try {
      setPreviewActionLoading(true);
      const res = isSimple
        ? await client.contracts.agreeToContract(sigId)
        : await client.contracts.signContract(sigId);
      // Update pending
      setPending(prev => prev.filter(c => c.signatureId !== sigId));
      setSuccessMessage("Contract accepted successfully!");
      if (onContractsUpdated) {
        onContractsUpdated();
      }
      closePreview();
    } catch (e) {
      console.error(e);
      setErrorMessage(e.message || 'Failed to complete action');
    } finally {
      setPreviewActionLoading(false);
    }
  };

  const handlePreviewDecline = async (sig, reason) => {
    const sigId = sig?.id || previewSignature?.id;
    try {
      setPreviewActionLoading(true);
      const res = await client.contracts.declineContract(sigId, reason);
      // Update pending
      setPending(prev => prev.filter(c => c.signatureId !== sigId));
      setSuccessMessage("Contract declined successfully!");
      if (onContractsUpdated) {
        onContractsUpdated();
      }
      closePreview();
    } catch (e) {
      console.error(e);
      setErrorMessage(e.message || 'Failed to decline');
    } finally {
      setPreviewActionLoading(false);
    }
  };

  const simpleContracts = pending.filter(c => c.isSimple);
  const allSelected = hasMultiple && selectedContracts.size === simpleContracts.length && simpleContracts.length > 0;
  const someSelected = selectedContracts.size > 0 && selectedContracts.size < simpleContracts.length;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          if (hasMandatorySimple && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return;
          }
          onClose();
        }} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            maxHeight: isMobile ? '100%' : '90vh',
            // iOS Safe Area Support
            ...(isMobile && {
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              height: '100vh',
              maxHeight: '100vh',
            }),
          }
        }}
      >
        {/* Header with gradient - respects notch */}
        <DialogTitle 
          sx={{ 
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            color: 'white',
            py: isMobile ? 2 : 3,
            px: isMobile ? 2 : 3,
            position: 'relative',
            // Extra padding for iOS notch
            ...(isMobile && {
              pt: 'max(16px, env(safe-area-inset-top, 0px))',
            }),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: alpha('#fff', 0.2),
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ContractIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
            </Box>
            <Box flex={1}>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>
                Pending Contracts
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
                {pending.length} {pending.length === 1 ? 'document' : 'documents'} requiring attention
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={loading || hasMandatorySimple}
              sx={{
                color: 'white',
                bgcolor: alpha('#fff', 0.1),
                '&:hover': { bgcolor: alpha('#fff', 0.2) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Select All checkbox for multiple simple contracts */}
          {hasMultiple && hasAnySimple && simpleContracts.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                    disabled={loading}
                    sx={{
                      color: 'white',
                      '&.Mui-checked': { color: 'white' },
                      '&.MuiCheckbox-indeterminate': { color: 'white' },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                    Select All Simple Contracts ({selectedContracts.size} selected)
                  </Typography>
                }
              />
            </Box>
          )}
        </DialogTitle>

        {/* Success/Error Messages */}
        {(successMessage || errorMessage) && (
          <Box sx={{ px: isMobile ? 2 : 3, pt: 2 }}>
            {successMessage && (
              <Alert severity="success" onClose={() => setSuccessMessage(null)}>
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage(null)}>
                {errorMessage}
              </Alert>
            )}
          </Box>
        )}

        {/* Content with modern card design - scrollable area */}
        <DialogContent 
          sx={{ 
            p: isMobile ? 2 : 3,
            bgcolor: theme.palette.grey[50],
            // Ensure content is scrollable and respects safe areas
            ...(isMobile && {
              flex: 1,
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
            }),
          }}
        >
          <Stack spacing={isMobile ? 2 : 2.5}>
            {pending.slice(0, 5).map(({ assignmentId, signatureId, assignment, template, isSimple }, idx) => {
              const publicLink = buildPublicLink(assignmentId);
              const hasDueDate = assignment?.due_date;
              const hasMessage = assignment?.assignment_message;
              const isSelected = selectedContracts.has(signatureId);
              const isProcessing = processingContract === signatureId;

              return (
                <Box
                  key={signatureId || idx}
                  sx={{
                    p: isMobile ? 2 : 2.5,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: isSelected 
                      ? `0 0 0 2px ${theme.palette.primary.main}`
                      : '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {/* Card Header with Checkbox */}
                  <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
                    {hasMultiple && isSimple && (
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectContract(signatureId)}
                        disabled={loading}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                    
                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: 1.5,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <DocumentIcon 
                        sx={{ 
                          color: theme.palette.primary.main,
                          fontSize: isMobile ? 20 : 24,
                        }} 
                      />
                    </Box>
                    
                    <Box flex={1} minWidth={0}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5}>
                        <Typography 
                          variant={isMobile ? "body1" : "h6"} 
                          fontWeight={600}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: isMobile ? '1 1 100%' : 'initial',
                          }}
                        >
                          {template?.name || "Contract"}
                        </Typography>
                        <Chip
                          size="small"
                          label={isSimple ? "Terms" : "Contract"}
                          color={isSimple ? "info" : "primary"}
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                          }}
                        />
                      </Stack>

                      {template?.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {template.description}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  {/* Message & Due Date Section */}
                  {(hasMessage || hasDueDate) && (
                    <Stack spacing={1} mb={2}>
                      {hasMessage && (
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <MessageIcon 
                              sx={{ 
                                fontSize: 16, 
                                color: theme.palette.info.main,
                                mt: 0.25,
                              }} 
                            />
                            <Typography variant="body2" color="text.secondary" flex={1}>
                              {assignment.assignment_message}
                            </Typography>
                          </Stack>
                        </Box>
                      )}

                      {hasDueDate && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CalendarIcon 
                            sx={{ 
                              fontSize: 16, 
                              color: theme.palette.warning.main,
                            }} 
                          />
                          <Typography variant="body2" fontWeight={500} color="warning.main">
                            Due: {new Date(assignment.due_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  {/* Action Buttons */}
                  <Stack 
                    direction={isMobile ? "column" : "row"} 
                    spacing={1}
                    alignItems={isMobile ? "stretch" : "center"}
                  >
                    {isSimple ? (
                      // Simple contract: Quick accept button
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                          onClick={() => handleAcceptSingle(signatureId)}
                          disabled={loading || isProcessing}
                          fullWidth={isMobile}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                          }}
                        >
                          {isProcessing ? 'Accepting...' : 'Quick Accept'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<DocumentIcon />}
                          onClick={() => openPreview({ assignment, signatureId })}
                          disabled={loading}
                          fullWidth={isMobile}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<LaunchIcon />}
                          onClick={() => navigate(`/contract/${assignmentId}`)}
                          disabled={loading}
                          fullWidth={isMobile}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                          }}
                        >
                          View Details
                        </Button>
                      </>
                    ) : (
                      // Complex contract: Must open full page
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<DocumentIcon />}
                          onClick={() => openPreview({ assignment })}
                          disabled={loading}
                          fullWidth={isMobile}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<LaunchIcon />}
                          onClick={() => navigate(`/contract/${assignmentId}`)}
                          disabled={loading}
                          fullWidth={isMobile}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                          }}
                        >
                          Open & Sign
                        </Button>
                      </>
                    )}
                    
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Open in new tab">
                        <IconButton
                          size="small"
                          onClick={() => window.open(publicLink, "_blank", "noopener")}
                          disabled={loading}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy link">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(publicLink)}
                          disabled={loading}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}

            {/* More contracts indicator */}
            {pending.length > 5 && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  + {pending.length - 5} more {pending.length - 5 === 1 ? 'contract' : 'contracts'} pending
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>

        {/* Footer Actions - respects home indicator */}
        <DialogActions 
          sx={{ 
            p: isMobile ? 2 : 3, 
            bgcolor: 'white',
            borderTop: `1px solid ${theme.palette.divider}`,
            flexDirection: 'column',
            gap: 1,
            alignItems: 'stretch',
            // Extra padding for iOS home indicator
            ...(isMobile && {
              pb: 'max(16px, env(safe-area-inset-bottom, 0px))',
            }),
          }}
        >
          {/* Bulk Actions for Multiple Simple Contracts */}
          {hasMultiple && hasAnySimple && simpleContracts.length > 0 && (
            <Stack direction={isMobile ? "column" : "row"} spacing={1} width="100%">
              <Button
                variant="contained"
                color="success"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleAcceptAll}
                disabled={loading || selectedContracts.size === 0}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1,
                }}
              >
                {loading ? 'Processing...' : `Accept Selected (${selectedContracts.size})`}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={loading ? <CircularProgress size={16} /> : <CancelIcon />}
                onClick={handleDeclineAll}
                disabled={loading || selectedContracts.size === 0 || hasSelectedMandatory}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1,
                }}
              >
                {loading ? 'Processing...' : `Decline Selected (${selectedContracts.size})`}
              </Button>
            </Stack>
          )}

          {/* Standard Actions */}
          <Stack direction={isMobile ? "column-reverse" : "row"} spacing={1} width="100%" justifyContent="space-between">
            <Button 
              onClick={onClose}
              disabled={loading || hasMandatorySimple}
              fullWidth={isMobile}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Remind Me Later
            </Button>
            {!hideReviewAll && (
              <Button
                variant="contained"
                startIcon={<ContractIcon />}
                onClick={handleViewAll}
                disabled={loading}
                fullWidth={isMobile}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1,
                }}
              >
                Review All Contracts
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog - also respects safe areas */}
      <EnhancedSignDialog
        open={previewOpen}
        assignment={previewAssignment}
        signature={previewSignature}
        contractData={previewContractData}
        loading={previewLoading}
        onClose={closePreview}
        onAgree={handlePreviewAgree}
        onDecline={handlePreviewDecline}
        actionLoading={previewActionLoading}
        readOnly={false}
        onNavigateToContract={() => {
          const assignmentId = previewAssignment?.id;
          if (assignmentId) {
            closePreview();
            navigate(`/contract/${assignmentId}`);
          }
        }}
      />
    </>
  );
}

// EnhancedSignDialog component with iOS safe area support
const EnhancedSignDialog = ({ 
  open, 
  assignment,
  signature, 
  contractData, 
  loading: contractLoading, 
  onClose, 
  onAgree, 
  onDecline, 
  actionLoading, 
  readOnly,
  onNavigateToContract 
}) => {
  const [declineMode, setDeclineMode] = useState(false);
  const [reason, setReason] = useState('');
  const contentRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!open) {
      setDeclineMode(false);
      setReason('');
    }
  }, [open]);

  if (!assignment) return null;

  const template = assignment?.template || {};
  const isSimple = assignment?.is_simple;
  const assignmentId = assignment?.id;
  const publicLink = assignmentId ? buildPublicLink(assignmentId) : null;

  const processedBody = contractData?.processed_contract_body || 
                       contractData?.template?.contract_body || 
                       assignment.processed_contract_body || 
                       template.contract_body || 
                       '';

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf')
    ]);

    const canvas = await html2canvas(contentRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let position = 0;
    let heightLeft = imgHeight;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    pdf.save(`${template?.name || 'contract'}.pdf`);
  };

  const showActionButtons = !readOnly;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100%' : '90vh',
          // iOS Safe Area Support
          ...(isMobile && {
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            height: '100vh',
            maxHeight: '100vh',
          }),
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          pr: 6,
          // Extra padding for iOS notch
          ...(isMobile && {
            pt: 'max(16px, env(safe-area-inset-top, 0px))',
          }),
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <DocumentIcon color="primary" />
          {template?.name || 'Contract'}
          <Chip size="small" label="Preview" color="info" variant="outlined" />
          {publicLink && (
            <Tooltip title="Open public link">
              <IconButton
                size="small"
                sx={{ ml: 'auto' }}
                onClick={() => window.open(publicLink, '_blank', 'noopener')}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        dividers 
        sx={{ 
          backgroundColor: '#fafafa',
          // Ensure content is scrollable and respects safe areas
          ...(isMobile && {
            flex: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          }),
        }}
      >
        {assignment?.assignment_message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2"><strong>Message:</strong> {assignment.assignment_message}</Typography>
          </Alert>
        )}
        {assignment?.due_date && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Due Date:</strong> {new Date(assignment.due_date).toLocaleString()}
            </Typography>
          </Alert>
        )}

        {contractLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading contract...</Typography>
          </Box>
        ) : (
          <Box
            ref={contentRef}
            sx={{
              position: 'relative',
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              mb: 3,
              backgroundColor: '#fff',
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.6
            }}
          >
            {processedBody ? (
              <div dangerouslySetInnerHTML={{ __html: processedBody }} />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography variant="body2" color="text.secondary">
                  Contract content not available
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {!readOnly && declineMode && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Please provide a reason for declining:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter your reason..."
              sx={{ mb: 2 }}
            />
          </Box>
        )}

        <Alert severity="info">
          <Typography variant="body2">
            {isSimple
              ? 'By clicking "I Agree", you confirm that you have read and agree to be bound by the terms above.'
              : 'By clicking "Sign Contract", you agree to be legally bound by the terms and conditions above. Your digital acceptance will be recorded.'}
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 3,
          // Extra padding for iOS home indicator
          ...(isMobile && {
            pb: 'max(24px, env(safe-area-inset-bottom, 0px))',
          }),
        }}
      >
        <Button 
          onClick={onNavigateToContract} 
          startIcon={<LaunchIcon />} 
          disabled={actionLoading}
          variant="outlined"
        >
          Open Full Page
        </Button>
        <Button onClick={downloadPdf} startIcon={<PdfIcon />} disabled={actionLoading}>
          Download PDF
        </Button>
        <Button onClick={onClose} disabled={actionLoading}>Close</Button>

        {!readOnly && (
          !declineMode ? (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeclineMode(true)}
                disabled={actionLoading}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                onClick={() => onAgree(signature, isSimple)}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={16} /> : <DocumentIcon />}
              >
                {actionLoading ? 'Processing...' : isSimple ? 'I Agree' : 'Sign Contract'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={() => onDecline(signature, reason)}
              disabled={!reason.trim() || actionLoading}
              startIcon={actionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
            >
              {actionLoading ? 'Submitting...' : 'Confirm Decline'}
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContractPopup;